import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithRef, ReactNode } from 'react'
import s from './InfoNote.module.css'

/**
 * Note explicative en ligne — la ligne grise du prototype, sous un contrôle ou une
 * grille : « Format parcelle : 2 lettres + 4 chiffres », « Par défaut le nom de la
 * personne présente ».
 *
 * ⚠️ **L'icône est une prop, pas un enfant**, et c'est un piège qui a déjà coûté un défaut
 * visible : la note est une boîte flex, donc *chaque* enfant en devient un élément séparé
 * par le `gap` — un `<b>` au milieu d'une phrase se retrouvait détaché de ses voisins, et
 * la ponctuation flottait après un blanc. Le composant range donc l'icône d'un côté et
 * **tout le texte dans un seul élément** de l'autre : la phrase se compose alors
 * normalement, gras et ponctuation compris.
 *
 * ⚠️ Ne pas s'en servir pour signaler un problème — c'est le rôle de `Banner`, qui a un
 * fond et un cadre, donc une chance d'être vu.
 */
const infoNote = cva(s.root, {
  variants: {
    tone: {
      neutral: s.toneNeutral,
      warn: s.toneWarn,
    },
  },
  defaultVariants: { tone: 'neutral' },
})

export interface InfoNoteProps
  extends Omit<ComponentPropsWithRef<'p'>, 'children'>, VariantProps<typeof infoNote> {
  /** Décorative : le texte se suffit. Posée `aria-hidden` par le composant. */
  icon?: ReactNode
  children?: ReactNode
}

export function InfoNote({ tone, icon, children, className, ...props }: InfoNoteProps) {
  return (
    <p className={infoNote({ tone, className })} {...props}>
      {icon !== undefined && (
        <span className={s.icon} aria-hidden>
          {icon}
        </span>
      )}
      <span className={s.text}>{children}</span>
    </p>
  )
}
